import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { clearAuthStorage, getAccessToken, setAuthTokens } from '../lib/authStorage';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  if (detail?.detail) return formatApiError(detail.detail);
  if (detail?.message) return detail.message;
  if (detail?.error) return typeof detail.error === 'string' ? detail.error : formatApiError(detail.error);
  return String(detail);
}

function formatRequestError(e, fallback = 'Something went wrong. Please try again.') {
  const status = e.response?.status;
  const apiDetail = e.response?.data?.detail ?? e.response?.data?.message ?? e.response?.data?.error ?? e.response?.data;
  if (!apiDetail && e.message === 'Network Error') {
    return 'Unable to reach the server. Please check your connection and try again.';
  }
  if (!apiDetail && status >= 500) {
    return 'The server could not complete this request right now. Please try again in a moment.';
  }
  return formatApiError(apiDetail) || e.message || fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      clearAuthStorage();
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      clearAuthStorage();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.requires_login_otp) {
        return { success: false, requires_login_otp: true, temp_token: data.temp_token, email: data.email, message: data.message };
      }
      if (data.requires_2fa) {
        return { success: false, requires_2fa: true, temp_token: data.temp_token, message: data.message };
      }
      if (data.requires_email_otp) {
        return { success: false, requires_email_otp: true, temp_token: data.temp_token, email: data.email, message: data.message };
      }
      setAuthTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      // Fetch full user from /auth/me so premium flags are always complete
      await checkAuth();
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };

  const verifyEmailOtp = async (tempToken, code) => {
    try {
      const { data } = await api.post('/auth/verify-email-otp', { temp_token: tempToken, code });
      setAuthTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      await checkAuth();
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };

  const resendEmailOtp = async (tempToken) => {
    try {
      await api.post('/auth/resend-email-otp', { temp_token: tempToken });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };

  const verifyLoginOtp = async (tempToken, code) => {
    try {
      const { data } = await api.post('/auth/verify-login-otp', { temp_token: tempToken, code });
      setAuthTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      await checkAuth();
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };

  const resendLoginOtp = async (tempToken) => {
    try {
      await api.post('/auth/resend-login-otp', { temp_token: tempToken });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };

  const verify2FA = async (tempToken, code) => {
    try {
      const { data } = await api.post('/auth/verify-2fa', { temp_token: tempToken, code });
      setAuthTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      await checkAuth();
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await api.post('/auth/register', userData);
      setAuthTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      await checkAuth();
      return { success: true };
    } catch (e) {
      return { success: false, error: formatRequestError(e) };
    }
  };


  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    clearAuthStorage();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2FA, verifyEmailOtp, resendEmailOtp, verifyLoginOtp, resendLoginOtp, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
